import { QueryClient } from '@tanstack/react-query';

/**
 * Optimized query client configuration for better performance
 */
export const createOptimizedQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Cache data for 5 minutes to reduce unnecessary requests
        staleTime: 5 * 60 * 1000,
        // Keep data in cache for 10 minutes
        gcTime: 10 * 60 * 1000,
        // Don't refetch on window focus to reduce server load
        refetchOnWindowFocus: false,
        // Only retry once on failure
        retry: 1,
        // Don't retry on 4xx errors
        retryOnMount: true,
        refetchOnReconnect: true,
      },
      mutations: {
        // Retry mutations once
        retry: 1,
      },
    },
  });
};

/**
 * Query key factories for consistent caching
 */
export const queryKeys = {
  // Products
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.products.lists(), filters] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.products.details(), id] as const,
  },
  
  // Orders
  orders: {
    all: ['orders'] as const,
    lists: () => [...queryKeys.orders.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.orders.lists(), filters] as const,
    details: () => [...queryKeys.orders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.orders.details(), id] as const,
  },
  
  // Customers
  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.customers.lists(), filters] as const,
    details: () => [...queryKeys.customers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.customers.details(), id] as const,
  },
  
  // Webshops
  webshops: {
    all: ['webshops'] as const,
    lists: () => [...queryKeys.webshops.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.webshops.lists(), filters] as const,
    details: () => [...queryKeys.webshops.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.webshops.details(), id] as const,
    accessible: (userId: string) => [...queryKeys.webshops.all, 'accessible', userId] as const,
  },
  
  // User data
  user: {
    all: ['user'] as const,
    profile: (userId: string) => [...queryKeys.user.all, 'profile', userId] as const,
    role: (userId: string) => [...queryKeys.user.all, 'role', userId] as const,
    permissions: (userId: string) => [...queryKeys.user.all, 'permissions', userId] as const,
  },
} as const;

/**
 * Performance optimization utilities for React Query
 */
export const queryOptimizations = {
  /**
   * Prefetch data for better UX
   */
  prefetchData: async (queryClient: QueryClient, key: string[], fetcher: () => Promise<any>) => {
    await queryClient.prefetchQuery({
      queryKey: key,
      queryFn: fetcher,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  },

  /**
   * Invalidate related queries efficiently
   */
  invalidateRelated: async (queryClient: QueryClient, baseKey: readonly string[]) => {
    await queryClient.invalidateQueries({
      queryKey: baseKey,
      exact: false,
    });
  },

  /**
   * Remove specific query from cache
   */
  removeQuery: (queryClient: QueryClient, key: readonly string[]) => {
    queryClient.removeQueries({
      queryKey: key,
      exact: true,
    });
  },

  /**
   * Set query data optimistically
   */
  setOptimisticData: <T>(
    queryClient: QueryClient,
    key: readonly string[],
    updater: (old: T | undefined) => T
  ) => {
    queryClient.setQueryData(key, updater);
  },

  /**
   * Batch multiple query updates
   * Note: React Query handles batching automatically, so we just execute updates sequentially
   */
  batchUpdates: (_queryClient: QueryClient, updates: Array<() => void>) => {
    updates.forEach(update => update());
  },
};

/**
 * Hook for optimized infinite queries
 */
export const createInfiniteQueryOptions = <T>(
  queryKey: readonly string[],
  fetcher: ({ pageParam }: { pageParam: number }) => Promise<{ data: T[]; nextPage?: number }>,
  options?: {
    pageSize?: number;
    staleTime?: number;
    gcTime?: number;
  }
) => {
  const { pageSize = 20, staleTime = 5 * 60 * 1000, gcTime = 10 * 60 * 1000 } = options || {};

  return {
    queryKey,
    queryFn: fetcher,
    initialPageParam: 0,
    getNextPageParam: (lastPage: { data: T[]; nextPage?: number }) => {
      return lastPage.data.length === pageSize ? lastPage.nextPage : undefined;
    },
    staleTime,
    gcTime,
    refetchOnWindowFocus: false,
    retry: 1,
  };
};

/**
 * Debounced query hook for search functionality
 */
export const createDebouncedQueryOptions = <T>(
  queryKey: readonly string[],
  fetcher: () => Promise<T>,
  searchTerm: string,
  _debounceMs: number = 300
) => {
  return {
    queryKey: [...queryKey, searchTerm],
    queryFn: fetcher,
    enabled: searchTerm.length >= 2, // Only search with 2+ characters
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  };
};